import { collection, getDocs, getFirestore, query, where } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useEffect, useState } from 'react';

import { User } from '../types';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasPermissions, setHasPermissions] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const db = getFirestore();

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        let hasPermissions = false;
        let permissions = {
          manage_polls: false,
          conduct_polls: false,
          validate_polls: false,
          view_dashboard: false,
          view_throwing: false,
          manage_users: false,
          view_voters: false,
          manage_voters: false,
          manage_organizations: false,
          view_voter_groups: false,
          manage_voter_groups: false,
          delete_voter_groups: false,
          view_precincts: false,
          view_barangays: false,
          view_responses: false,
          view_reports: false,
          tv_monitor: false,
          admin: false,
          scan_qr_stub_release: false,
          scan_qr_final_release: false,
          scan_qr_exit_poll: false,
          view_exit_polls: false,
          analytics: false
        };

        let allowedTabulations = undefined;
        let allowedPolls = undefined;
        let allowedContests = undefined;
        let dashboard = "";

        // Query users collection by email instead of using uid directly
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('email', '==', firebaseUser.email));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          // Get the first matching document (assuming emails are unique)
          const userDoc = querySnapshot.docs[0];
          const userData = userDoc.data();

          if (userData.permissions) {
            permissions = {
              manage_polls: userData.permissions.manage_polls ?? false,
              conduct_polls: userData.permissions.conduct_polls ?? false,
              validate_polls: userData.permissions.validate_polls ?? false,
              view_dashboard: userData.permissions.view_dashboard ?? false,
              view_throwing: userData.permissions.view_throwing ?? false,
              manage_users: userData.permissions.manage_users ?? false,
              view_voters: userData.permissions.view_voters ?? false,
              manage_voters: userData.permissions.manage_voters ?? false,
              manage_organizations: userData.permissions.manage_organizations ?? false,
              view_voter_groups: userData.permissions.view_voter_groups ?? false,
              manage_voter_groups: userData.permissions.manage_voter_groups ?? false,
              delete_voter_groups: userData.permissions.delete_voter_groups ?? false,
              view_precincts: userData.permissions.view_precincts ?? false,
              view_barangays: userData.permissions.view_barangays ?? false,
              view_responses: userData.permissions.view_responses ?? false,
              view_reports: userData.permissions.view_reports ?? false,
              tv_monitor: userData.permissions.tv_monitor ?? false,
              scan_qr_stub_release: userData.permissions.scan_qr_stub_release ?? false,
              scan_qr_final_release: userData.permissions.scan_qr_final_release ?? false,
              scan_qr_exit_poll: userData.permissions.scan_qr_exit_poll ?? false,
              view_exit_polls: userData.permissions.view_exit_polls ?? false,
              admin: userData.permissions.admin ?? false,
              analytics: userData.permissions.analytics ?? false
            };
            hasPermissions = Object.values(permissions).some(Boolean);
          }

          if (userData.allowedTabulations) {
            allowedTabulations = userData.allowedTabulations;
          }

          if (userData.allowedPolls) {
            allowedPolls = userData.allowedPolls;
          }

          if (userData.dashboard) {
            dashboard = userData.dashboard;
          }

          if (userData.allowedContests) {
            allowedContests = userData.allowedContests;
          }
        }

        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email ?? '',
          display_name: firebaseUser.displayName ?? undefined,
          permissions,
          allowedTabulations,
          allowedPolls,
          allowedContests,
          dashboard
        });
        setHasPermissions(hasPermissions);
      } else {
        setUser(null);
        setHasPermissions(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return { user, loading, hasPermissions };
};

export default useAuth;
